<template lang="pug">
tr.vue-time-entries-row.time-entry-row(
  :class="timeEntryRowClass"
  data-testid="time-entry-row"
)
  td
    AppCheckbox(
      v-tooltip
      :value="checked"
      :disabled="!isDeletable(timeEntry)"
      :title="disabledDeleteTooltip(timeEntry)"
      @input="updateTimeEntriesToBatchDelete"
    )
    // ^ Batch edit is disabled when the record can't be edited, see #deletable_reasons
  td
    .project-cell-wrapper
      AppAvatarInitial(
        :text="projectName"
        :project-color="projectColor"
      )
        i.hi-work-break(v-if="timeEntry.work_break")
      .time-entry-data.cell-max-width.inline-block
        span.name.strong.ellipsis(:class="{ switcher: !timeEntry.modifiableTooltip }")
          .ellipsis(
            v-if="timeEntry.modifiableTooltip"
            data-container="body"
          )
            span {{ projectName }}
          TimeEntriesProjectSwitcherPopover(
            v-else
            :time-entry="timeEntry"
          )
            template(slot="trigger")
              .ellipsis.inline-flex
                span
                  | {{ projectName }}
        .strong.hi-10.org-name.is-uppercase.text-lighter.ellipsis
          | {{ organizationName }}
        .name.strong.ellipsis(:class="{ switcher: !timeEntry.modifiableTooltip }")
          span(
            v-if="timeEntry.modifiableTooltip"
            data-container="body"
          ) {{ taskName }}
          TimeEntriesTaskSwitcherPopover(
            v-else
            :time-entry="timeEntry"
          )
            template(slot="trigger")
              | {{ taskName }}
  td(v-if="tableColumns.show_reasons && reasonsOrNotesArePresent")
    table.reasons-table
      div(v-if="timeEntry.manual_time_request")
        tr(v-if="timeEntry.manual_time_request_reason")
          AppPopover(
            trigger="hover"
            lazy
          )
            span(slot="body")
              p {{ timeEntry.manual_time_request_reason | stripHTML }}
            span(slot="trigger")
              td.text-light
                .text-overflow
                  | {{ timeEntry.manual_time_request_reason | stripHTML }}
        tr(v-if="timeEntry.manual_time_request_note")
          AppPopover(
            trigger="hover"
            lazy
          )
            span(slot="body")
              p {{ timeEntry.manual_time_request_note | stripHTML }}
            span(slot="trigger")
              td.strong.pr-5 Note:
              td.text-light
                .text-overflow
                  | {{ timeEntry.manual_time_request_note | stripHTML }}
      div(v-else)
        tr(v-if="reasonsArePresent")
          AppPopover(
            trigger="hover"
            lazy
          )
            span(slot="body")
              p(
                v-for="note in uniqueReasonNotes"
                :key="note"
              )
                | {{ note | stripHTML }}
            span(slot="trigger")
              td.text-light
                .text-overflow
                  | {{ reasons[0].note | stripHTML }}
        tr(v-if="notesArePresent")
          AppPopover(
            trigger="hover"
            lazy
          )
            span(slot="body")
              p(
                v-for="note in notes"
                :key="note.description"
              )
                | {{ note.description | stripHTML }}
            span(slot="trigger")
              td.strong.pr-5 Note:
              td.text-light
                .text-overflow
                  | {{ notes[0].description | stripHTML }}
        tr(v-if="!reasonsArePresent && !notesArePresent")
          td —
  td.activitiy-status(v-if="tableColumns.show_activity && showActivityColumns")
    template(v-if="timeEntry.work_break") -
    .inline-block(
      v-else
      v-tooltip="true"
      :title="formatDuration(timeEntry.overall)"
    )
      | {{ timeEntry.overall | percentage(timeEntry.input_tracked) }}
  td.idle-status(v-if="tableColumns.show_idle && showActivityColumns")
    template(v-if="timeEntry.work_break") -
    .inline-block(
      v-else
      v-tooltip="true"
      :title="formatDuration(timeEntry.idle)"
    )
      | {{ timeEntry.idle | percentage(timeEntry.tracked) }}
  td.manual-status
    template(v-if="timeEntry.work_break") -
    .inline-block(v-else-if="timeEntry.manual_time_request")
      i.hi.hi-14.text-orange.hi-warning.mr-10(
        v-tooltip
        data-original-title="This manual time request needs approval"
      )
      | {{ timeEntry.manual | percentage(timeEntry.tracked) }}
    .inline-block(
      v-else
      v-tooltip="true"
      :title="formatDuration(timeEntry.manual)"
    )
      | {{ timeEntry.manual | percentage(timeEntry.tracked) }}

  td.duration-status
    .inline-block(
      v-if="timeEntry.editDisabled"
      v-tooltip="tooltipDisabledEditOptions"
      data-container="body"
      :data-original-title="timeEntry.modifiableTooltip"
    )
      TimeEntriesOvertimePopover(
        :time-entries="[timeEntry]"
        :organization="organization"
        :show-financial="isOwner"
        icon-classes="mr-5"
        container-classes="absolute overtime-popover-row"
      )
      | {{ formattedDuration }}
      i.fa.fa-dollar.a-tooltip.ml-5(
        v-if="timeEntry.manual_time_request && timeEntry.billable"
        v-tooltip="{ placement: 'right' }"
        title="Billable time"
        data-testid="time-entries-row-dollar"
      )
    .d-flex.align-items-center(v-else)
      TimeEntriesOvertimePopover(
        :time-entries="[timeEntry]"
        :organization="organization"
        :show-financial="isOwner"
        icon-classes="mr-5"
        container-classes="absolute overtime-popover-row"
      )
      div(v-if="!isWorkBreak")
        TimeEntriesFormPopover(
          :time-entry="timeEntry"
          @open-edit-dialog="showEditedTimeEntriesFormDialog"
        )
          a.a-popover {{ formattedDuration }}
      div(v-if="isWorkBreak")
        span
          | {{ formattedDuration }}
      i.fa.fa-dollar.a-tooltip.ml-5(
        v-if="timeEntry.billable"
        v-tooltip="{ placement: 'right' }"
        title="Billable time"
        data-testid="time-entries-row-dollar"
      )
  td.source(v-if="showSourceColumn")
    div(
      v-for="(clientType, index) in timeEntryClientTypes"
      :key="`${clientType}-${index}`"
    )
      | {{ clientType }}
  td
    .inline-block(
      v-if="timeEntry.editDisabled"
      v-tooltip="tooltipDisabledEditOptions"
      data-container="body"
      :data-original-title="timeEntry.modifiableTooltip"
    )
      span
        | {{ formattedDatetimeRange }}
    div(v-else-if="!isWorkBreak")
      TimeEntriesFormPopover(
        :time-entry="timeEntry"
        @open-edit-dialog="showEditedTimeEntriesFormDialog"
      )
        a.a-popover {{ formattedDatetimeRange }}
    div(v-else-if="isWorkBreak")
      span
        | {{ formattedDatetimeRange }}
  td.screenshots(v-if="showScreenshotsColumn")
    TimeEntriesScreensDialog(
      :screenshots="timeEntry.screens"
      :show="timeEntry.id === openedTimeEntryIdScreensDialog"
      @hide-dialog="openedTimeEntryIdScreensDialog = null"
    )
    .screen-count-pill(
      :class="timeEntry.screens.length > 0 ? 'clickable' : 'not-allowed'"
      @click="timeEntry.screens.length > 0 && openScreensDialog(timeEntry)"
    )
      | {{ timeEntry.screens_count }} {{ timeEntry.screens_count | pluralize('screen', 'screens') }}

  td.actions
    TimeEntriesLockedPopover(
      v-if="showLocked(timeEntry)"
      trigger="hover"
      :reasons="timeEntry.allReasons"
    )
      .inline-block(slot="trigger")
        i.hi.hi-lock.mr-5
    .inline-block(
      v-else-if="isArchivedProject(project)"
      v-tooltip="{ html: true }"
      data-container="body"
      :data-original-title="timeEntry.modifiableTooltip"
    )
      a.action.btn.btn-sm.disabled.btn-default(@click.prevent="false") Archived
    .dropdown.table-actions-dropdown(v-else)
      a.btn.btn-sm.btn-default.dropdown-toggle(
        data-toggle="dropdown"
        role="button"
      )
        | Actions
        i.hi.hi-caret.hi-10
      ul.dropdown-menu.dropdown-menu-right
        li
          a.action(
            v-tooltip="{ placement: 'left', html: true }"
            :class="{ disabled: editDisabled, 'is-muted': editDisabled }"
            data-container="body"
            data-testid="edit-time-entry"
            :data-original-title="timeEntry.modifiableTooltip"
            @click.prevent="showFormDialog"
          ) Edit time entry
        li
          a.action(
            v-tooltip="{ placement: 'left', html: true }"
            :class="{ disabled: editDisabled, 'is-muted': editDisabled }"
            data-container="body"
            :data-original-title="timeEntry.modifiableTooltip"
            @click.prevent="showSplitDialog"
          ) Split time entry
        li
          a.action(
            v-tooltip
            :class="{ 'is-muted': disabledDelete, disabled: disabledDelete }"
            :title="timeEntry.deleteDisabledTooltip"
            data-container="body"
            @click.prevent="showDeleteDialog"
          ) Delete time entry
        li(v-if="timeEntry.manual_time_request")
          a.action(
            :href="manualTimeRequestUrl"
            target="_blank"
          ) View manual time request
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { isEmpty, includes } from 'lodash-es';
import { formatDatetimeRange, formatDuration } from '~/utils/time_utils';
import TimeEntriesLockedPopover from '../TimeEntriesLockedPopover.vue';
import TimeEntriesScreensDialog from '~/components/time_entries/TimeEntriesScreensDialog.vue';
import { MANUAL_TIME_REQUEST_TIME_ENTRY_COLOR } from '~/app_constants';

export default {
  name: 'TimeEntriesRow',
  components: {
    TimeEntriesLockedPopover,
    TimeEntriesScreensDialog,
  },
  props: {
    timeEntry: { type: Object, required: true },
    checked: { type: Boolean, default: false },
    reasonsOrNotesArePresent: { type: Boolean, required: true },
  },
  data() {
    return {
      showFilters: false,
      showTimeEntriesEditDialog: false,
      showTimeEntriesSplitDialog: false,
      showTimeEntriesDeleteDialog: false,
      editedTimeEntry: {},
      tooltipDisabledEditOptions: { placement: 'bottom', html: true },
      openedTimeEntryIdScreensDialog: null,
    };
  },
  computed: {
    ...mapState({
      isOwner: 'is_owner',
    }),
    ...mapGetters([
      'getOrganization',
      'getProject',
      'getTask',
      'organization',
      'managedProjectIds',
      'hasFeatureKeyboardAndMouseActivity',
      'currentZone',
      'isArchivedProject',
      'showActivityColumns',
      'tableColumns',
      'notesForTimeEntry',
      'reasonsForTimeEntry',
      'showLocked',
      'projectFlavour',
      'showSourceColumn',
      'isDeletable',
      'disabledDeleteTooltip',
      'showScreenshotsColumn',
      'preventOwnTimeDeletion',
    ]),
    timeEntryClientTypes() {
      return this.timeEntry.client_type_names;
    },
    editDisabled() {
      return this.timeEntry.editDisabled;
    },
    isWorkBreak() {
      return this.timeEntry.work_break;
    },
    timeEntryRowClass() {
      if (this.timeEntry.immutable) {
        return 'immutable';
      }

      if (this.isArchivedProject(this.project)) {
        return 'archived';
      }

      if (!includes(this.managedProjectIds, this.project.id)) {
        return 'unmanaged';
      }

      return '';
    },
    organizationName() {
      const organization = this.getOrganization(this.timeEntry.organization_id);

      return organization.text;
    },
    project() {
      return this.getProject(this.timeEntry.project_id);
    },
    projectName() {
      return this.timeEntry.work_break ? this.workBreakPolicyName : this.project.text;
    },
    workBreakPolicyName() {
      return this.timeEntry.work_break_policy_paid ? this.project.text + ' $' : this.project.text;
    },
    emptyTaskString() {
      if (this.timeEntry.work_break) {
        return 'Work break';
      }

      if (this.project.work_order) {
        return 'No job';
      }

      return 'No to-do';
    },
    taskName() {
      const task = this.getTask(this.timeEntry.task_id);

      return task ? task.text : this.emptyTaskString;
    },
    formattedDatetimeRange() {
      return formatDatetimeRange(this.timeEntry.start_time, this.timeEntry.stop_time, {
        style: 'time',
        show_seconds: 'auto',
        time_zone: this.currentZone.name,
      });
    },
    formattedDuration() {
      return this.formatDuration(this.timeEntry.tracked);
    },
    showActivityColumns() {
      return !this.organization || this.hasFeatureKeyboardAndMouseActivity;
    },
    notes() {
      return this.notesForTimeEntry(this.timeEntry);
    },
    notesArePresent() {
      return !isEmpty(this.notes);
    },
    reasons() {
      return this.reasonsForTimeEntry(this.timeEntry);
    },
    uniqueReasonNotes() {
      return [...new Set(this.reasonsForTimeEntry(this.timeEntry).map((r) => r.note))];
    },
    reasonsArePresent() {
      return !isEmpty(this.reasons);
    },
    projectColor() {
      return this.timeEntry.manual_time_request ? MANUAL_TIME_REQUEST_TIME_ENTRY_COLOR : '';
    },
    manualTimeRequestUrl() {
      return `/organizations/${this.organization.id}/manual_time_requests?status=submitted&user_id=${this.timeEntry.user_id}&time_zone=${encodeURIComponent(this.currentZone.name)}&manual_time_request_id=${this.timeEntry.manual_time_request_id}`;
    },
    disabledDelete() {
      return this.timeEntry.deleteDisabledTooltip || this.timeEntry.manual_time_request;
    },
  },
  methods: {
    openScreensDialog(timeEntry) {
      this.openedTimeEntryIdScreensDialog = timeEntry.id;
    },
    formatDuration(duration) {
      return formatDuration(duration, { showSeconds: 'auto' });
    },
    updateTimeEntriesToBatchDelete(value) {
      if (value) {
        this.$store.dispatch('addToTimeEntriesBatchDelete', this.timeEntry);
      } else {
        this.$store.dispatch('removeFromTimeEntriesBatchDelete', this.timeEntry);
      }
    },
    showEditedTimeEntriesFormDialog(timeEntry) {
      this.$store.dispatch('openDialog', { dialog: 'timeEntriesFormDialog', type: 'edit', timeEntry });
    },
    showFormDialog() {
      if (!this.editDisabled) {
        this.$store.dispatch('openDialog', {
          dialog: 'timeEntriesFormDialog',
          type: 'edit',
          timeEntry: this.timeEntry,
        });
      }
    },
    showSplitDialog() {
      if (!this.editDisabled) {
        this.$store.dispatch('openDialog', { dialog: 'timeEntriesSplitDialog', timeEntry: this.timeEntry });
      }
    },
    showDeleteDialog() {
      if (this.preventOwnTimeDeletion || this.disabledDelete) {
        return false;
      }

      this.$store.dispatch('openDialog', { dialog: 'timeEntriesDeleteDialog', timeEntry: this.timeEntry });
    },
  },
};
</script>
